/**
 * Single source of truth for dashboard metric labels + tooltip copy.
 * Keep in sync with docs/dashboard-v2/02-ADMIN-DASHBOARD.md.
 */

export interface MetricDef {
  id: string;
  label: string;
  description: string;
}

const defs = {
  // --- Overview ---
  platformNetPnl: {
    id: 'platformNetPnl',
    label: 'Platform Net P&L',
    description:
      'Platform earnings in the selected period: credits wholesaled to super agents (cash collected + online purchases and balance converts) plus system fees on all other flows. Downstream credit sales and shop counter cash are excluded — that revenue belongs to the tier that sold.',
  },
  feesEarned: {
    id: 'feesEarned',
    label: 'Fees Earned',
    description:
      'System fee revenue collected on completed gateway payments in the selected period. Gateway and blockchain network fees are pass-through costs and are not included.',
  },
  hierarchyCashCollected: {
    id: 'hierarchyCashCollected',
    label: 'Hierarchy Cash Collected',
    description:
      'Cash physically collected by the platform when selling credits to super agents in the selected period (admin→super_agent cash distribution).',
  },
  wholesaleOnlineUsd: {
    id: 'wholesaleOnlineUsd',
    label: 'Wholesale Online (SA)',
    description:
      'USD from super agents buying credits from the platform online in the selected period: CDN purchases plus balance-to-credits converts. Together with Hierarchy Cash Collected this is the platform\'s wholesale revenue.',
  },
  usdIn: {
    id: 'usdIn',
    label: 'USD In',
    description:
      'External money that entered the platform in the selected period: online player deposits plus online (CDN) credit purchases by super agents, agents, and shops. Balance-to-credits converts, cash counter entries, and wallet prefunds are not counted — they are not new money.',
  },
  payoutsOut: {
    id: 'payoutsOut',
    label: 'Payouts Out',
    description:
      'USD actually sent out of the platform in the selected period (net payout amounts): completed player withdrawals plus completed USD withdrawals by super agents, agents, and shops.',
  },
  netCashFlow: {
    id: 'netCashFlow',
    label: 'Net Cash Flow',
    description:
      'USD In minus Payouts Out for the selected period. Positive means more money came in than went out.',
  },
  creditsIssued: {
    id: 'creditsIssued',
    label: 'Credits Issued',
    description:
      'Game credits delivered in the selected period: credits from online player deposits, online (CDN) credit purchases, and balance-to-credits converts across the network.',
  },
  pendingWithdrawalsCount: {
    id: 'pendingWithdrawalsCount',
    label: 'Pending Withdrawals',
    description:
      'Player withdrawal requests waiting for review or payout right now. This is a live number, so it has no comparison with a previous period.',
  },
  pendingWithdrawalsAmount: {
    id: 'pendingWithdrawalsAmount',
    label: 'Pending Amount',
    description:
      'Total USD tied up in player withdrawal requests that are still waiting right now.',
  },
  shopFundingDebtOutstanding: {
    id: 'shopFundingDebtOutstanding',
    label: 'Shop Funding Debt',
    description:
      'Outstanding shop credit debts — fees or credits the platform fronted for shops that shops still owe. Live number.',
  },
  exposure: {
    id: 'exposure',
    label: 'Exposure',
    description:
      'What the platform would owe if everything stopped now: player credit liability plus pending withdrawals plus online debt outstanding.',
  },

  // --- Platform-Players ---
  depositsAmount: {
    id: 'depositsAmount',
    label: 'Deposits (net USD)',
    description:
      'Total net USD deposited by independent (platform) players through online payment gateways completed in the selected period.',
  },
  depositsCount: {
    id: 'depositsCount',
    label: 'Deposit Count',
    description: 'Number of completed platform-player deposits in the selected period.',
  },
  avgDeposit: {
    id: 'avgDeposit',
    label: 'Avg Deposit',
    description: 'Average size of a completed platform-player deposit in the selected period.',
  },
  depositFailedRatePct: {
    id: 'depositFailedRatePct',
    label: 'Failed Rate',
    description:
      'Share of platform-player deposit attempts started in the selected period that ended failed, expired, cancelled, or rejected.',
  },
  withdrawalsAmount: {
    id: 'withdrawalsAmount',
    label: 'Withdrawals (net USD)',
    description:
      'Total net USD paid out to independent platform players in withdrawals completed in the selected period.',
  },
  withdrawalsCount: {
    id: 'withdrawalsCount',
    label: 'Withdrawal Count',
    description: 'Number of platform-player withdrawals completed in the selected period.',
  },
  playersPendingWithdrawals: {
    id: 'playersPendingWithdrawals',
    label: 'Pending Withdrawals',
    description:
      'Platform-player withdrawal requests still waiting right now. Live number, no period comparison.',
  },
  playerCreditLiability: {
    id: 'playerCreditLiability',
    label: 'Player Credit Liability',
    description:
      'Total game credits currently sitting in player wallets — what the platform would owe if every player cashed out right now. Live number.',
  },

  // --- Shop-Players ---
  playerMoneyIn: {
    id: 'playerMoneyIn',
    label: 'Player Money In',
    description:
      'Gross money from shop-linked players in the selected period: online deposits (net USD) plus counter cash received at shop tills.',
  },
  shopNetProfit: {
    id: 'shopNetProfit',
    label: 'Shop Net Profit',
    description:
      'Player money in minus shop spent in the selected period. Spent includes credit purchases, cash paid upstream, payout funding, and counter redemptions paid.',
  },
  shopSpent: {
    id: 'shopSpent',
    label: 'Shop Spent',
    description:
      'Total outflows funding shop-player activity in the selected period: credit purchases, cash to agent, payout funding, and counter redemptions paid.',
  },
  onlineDepositsNetUsd: {
    id: 'onlineDepositsNetUsd',
    label: 'Online Deposits',
    description: 'Net USD from shop-linked players via online gateways in the selected period.',
  },
  onlineDepositsCount: {
    id: 'onlineDepositsCount',
    label: 'Online Deposit Count',
    description: 'Number of completed online deposits by shop-linked players in the selected period.',
  },
  counterDepositsCashReceived: {
    id: 'counterDepositsCashReceived',
    label: 'Counter Deposits (cash)',
    description: 'Cash received at shop counters from shop-linked player deposits in the selected period.',
  },
  counterDepositsCredits: {
    id: 'counterDepositsCredits',
    label: 'Counter Deposits (credits)',
    description: 'Game credits issued at shop counters for player deposits in the selected period.',
  },
  onlineWithdrawalsGrossUsd: {
    id: 'onlineWithdrawalsGrossUsd',
    label: 'Online Withdrawals',
    description: 'Gross USD paid out to shop-linked players via online withdrawals in the selected period.',
  },
  onlineWithdrawalsCount: {
    id: 'onlineWithdrawalsCount',
    label: 'Online Withdrawal Count',
    description: 'Number of completed online withdrawals by shop-linked players in the selected period.',
  },
  counterRedemptionsCashPaid: {
    id: 'counterRedemptionsCashPaid',
    label: 'Counter Redemptions (cash paid)',
    description: 'Cash paid out at shop counters for player redemptions in the selected period.',
  },
  counterRedemptionsCredits: {
    id: 'counterRedemptionsCredits',
    label: 'Counter Redemptions (credits)',
    description: 'Game credits redeemed at shop counters in the selected period.',
  },
  tierFundingUsd: {
    id: 'tierFundingUsd',
    label: 'Tier Funding of Payouts',
    description:
      'USD funded by the agent hierarchy (shop/agent/super agent) for shop-linked player withdrawals in the selected period — an early warning when shops rely on upstream funding.',
  },

  // --- Agent Network ---
  creditPurchasesTotal: {
    id: 'creditPurchasesTotal',
    label: 'Credit Purchases',
    description:
      'Total USD the agent network (super agents, agents, shops) paid for game credits in the selected period, via gateway payments or USD wallet balance. Legacy balance converts without a buyer tag count here but not in the per-tier split.',
  },
  creditPurchasesSuperAgent: {
    id: 'creditPurchasesSuperAgent',
    label: 'By Super Agents',
    description: 'Credit purchases paid by super agents in the selected period (current window only).',
  },
  creditPurchasesAgent: {
    id: 'creditPurchasesAgent',
    label: 'By Agents',
    description: 'Credit purchases paid by agents in the selected period (current window only).',
  },
  creditPurchasesShop: {
    id: 'creditPurchasesShop',
    label: 'By Shops',
    description: 'Credit purchases paid by shops in the selected period (current window only).',
  },
  platformFeeRevenue: {
    id: 'platformFeeRevenue',
    label: 'Platform Fee Revenue',
    description: 'System fees collected on network credit purchases in the selected period.',
  },
  cashExpected: {
    id: 'cashExpected',
    label: 'Cash Expected',
    description:
      'Cash the network was expected to hand over for credit sales recorded in the selected period (offline settlement).',
  },
  cashReceived: {
    id: 'cashReceived',
    label: 'Cash Received',
    description:
      'Cash actually received for credit sales recorded in the selected period. If this trails Cash Expected, someone has not settled yet.',
  },
  cashUncollected: {
    id: 'cashUncollected',
    label: 'Cash Uncollected',
    description: 'Cash Expected minus Cash Received in the selected period — unsettled cash risk.',
  },
  onlineDebtOutstanding: {
    id: 'onlineDebtOutstanding',
    label: 'Outstanding Online Debt',
    description:
      'Fees the platform advanced for the network that shops, agents, or super agents still have to settle. Live number.',
  },
  tierUsdFloatTotal: {
    id: 'tierUsdFloatTotal',
    label: 'Tier USD Float',
    description:
      'USD currently sitting in super agent, agent, and shop USD wallets (spendable balance). Live number.',
  },

  // --- Charts ---
  depositsByProvider: {
    id: 'depositsByProvider',
    label: 'Deposits by Provider',
    description:
      'Completed platform-player deposit volume in the selected period split by payment provider.',
  },
  dailyFlow: {
    id: 'dailyFlow',
    label: 'Daily Cash Flow',
    description:
      'USD In (bars) versus Payouts Out (bars) with the daily net as a line, one point per day of the selected period.',
  },

  // --- Overview (executive summary) charts ---
  cashFlowTrend: {
    id: 'cashFlowTrend',
    label: 'Cash Flow Trend',
    description:
      'Money entering versus leaving the platform over the selected period — bars for USD In and Payouts Out, a line for the net. Today is bucketed by hour; week and month by day. The bars add up exactly to the USD In and Payouts Out cards above.',
  },
  revenueMix: {
    id: 'revenueMix',
    label: 'Where Profit Comes From',
    description:
      'Platform Net P&L split by source: cash collected when selling credits to super agents, online credit sales to super agents, and system fees on every other flow. The three slices always add up to the Platform Net P&L headline.',
  },
  feesBySource: {
    id: 'feesBySource',
    label: 'Fees Earned by Source',
    description:
      'System fee revenue over time, split by the flow that generated it: player deposits, withdrawals, and credit purchases by the agent network. Gateway and blockchain network fees are pass-through costs and are excluded.',
  },
  moneyInBySource: {
    id: 'moneyInBySource',
    label: 'Money In by Source',
    description:
      'External money entering the platform over time, split three ways: deposits by independent players, deposits by shop-linked players, and online credit purchases by the agent network. Cash at shop counters is not platform money and is excluded.',
  },
  depositsByGateway: {
    id: 'depositsByGateway',
    label: 'Deposits by Gateway',
    description:
      'Gross deposit volume in the selected period ranked by payment gateway, across both independent and shop-linked players. Useful for spotting a provider outage or a shift in player payment habits.',
  },
  creditSalesByTier: {
    id: 'creditSalesByTier',
    label: 'Credit Sales by Tier',
    description:
      'USD the agent network paid for credits in the selected period, split by who bought: super agents, agents, or shops. Purchases without a recorded buyer tier are not shown.',
  },
  exposureBreakdown: {
    id: 'exposureBreakdown',
    label: 'Exposure Breakdown',
    description:
      'What the platform owes right now, broken down: credits sitting in player wallets, withdrawals waiting to be paid, and online debt owed to tiers. Shop funding debt is money owed *to* the platform and is shown alongside for context. Live numbers, not period-scoped.',
  },
  cashSettlementHealth: {
    id: 'cashSettlementHealth',
    label: 'Cash Settlement Health',
    description:
      'How much of the cash owed for credit sales recorded in the selected period has actually been handed over. A gap means someone in the hierarchy has taken credits but not yet settled the cash.',
  },

  // --- Player population ---
  totalPlayers: {
    id: 'totalPlayers',
    label: 'Total Players',
    description:
      'Every registered player account, all-time — active, inactive and banned included. Live number, not scoped to the selected period.',
  },
  newPlayers: {
    id: 'newPlayers',
    label: 'New Players',
    description:
      'Player accounts registered during the selected period, compared against the same length of time immediately before it.',
  },
  activePlayers: {
    id: 'activePlayers',
    label: 'Active Players',
    description:
      'Distinct players who either moved money or signed in during the selected period. Same definition the daily platform report uses, so the two always agree.',
  },
  playerActivity: {
    id: 'playerActivity',
    label: 'Player Activity',
    description:
      'New signups (bars) against active players (line) over the selected period. A widening gap between them means acquisition is outpacing engagement.',
  },
  playerGrowth: {
    id: 'playerGrowth',
    label: 'Player Growth',
    description:
      'Cumulative registered players across the period, with new signups and active players overlaid. The population curve is derived from the live total working backwards through signups.',
  },

  // --- Per-tab charts ---
  depositsVsWithdrawals: {
    id: 'depositsVsWithdrawals',
    label: 'Deposits vs Withdrawals',
    description:
      'Money this player segment put in (net USD after fees) against what was paid back out, one bar pair per bucket of the selected period.',
  },
  creditsIssuedTrend: {
    id: 'creditsIssuedTrend',
    // Distinct from the `creditsIssued` KPI label — they appear on the same tab.
    label: 'Credits Issued Over Time',
    description:
      'Game credits delivered per bucket — from online player deposits, network credit purchases, and balance-to-credits converts.',
  },
  liabilityMix: {
    id: 'liabilityMix',
    label: 'Liability Mix',
    description:
      'What the platform currently owes, broken into player credits, withdrawals waiting to be paid, and online debt owed to tiers. Live numbers.',
  },
  depositFunnel: {
    id: 'depositFunnel',
    label: 'Deposit Funnel',
    description:
      'Deposit attempts started in the selected period split by outcome: completed, or failed / expired / cancelled / rejected. A rising failure share usually means a gateway problem, not player behaviour.',
  },
  shopChannelMix: {
    id: 'shopChannelMix',
    label: 'Online vs Counter',
    description:
      'Shop-player money movement per bucket across both channels: online gateway deposits and withdrawals, and cash taken or paid at the shop counter.',
  },
  shopMoneyInMix: {
    id: 'shopMoneyInMix',
    label: 'Money In by Channel',
    description:
      'Share of shop-player money arriving online versus as cash at the counter for the selected period.',
  },
  purchasesByTierTrend: {
    id: 'purchasesByTierTrend',
    label: 'Credit Purchases by Tier',
    description:
      'USD the network spent on credits per bucket, stacked by who bought — super agents, agents, or shops. Purchases without a recorded buyer tier are not shown.',
  },
  topAgentsByPurchases: {
    id: 'topAgentsByPurchases',
    label: 'Top Agents by Credits Purchased',
    description:
      'Agents ranked by credit purchase volume in the selected period (CDN, cash, and balance conversion). Shows concentration across the whole platform without switching to a super-agent portal.',
  },
  topShopsByPurchases: {
    id: 'topShopsByPurchases',
    label: 'Top Shops by Credits Purchased',
    description:
      'Shops ranked by credit purchase volume in the selected period (CDN, cash, and balance conversion). Shows where retail credit demand concentrates across the platform.',
  },
  cashSettlementTrend: {
    id: 'cashSettlementTrend',
    label: 'Cash Expected vs Received',
    description:
      'Cash owed for credit sales recorded in each bucket against what was actually collected. A persistent gap is unsettled cash risk.',
  },
} as const;

export type MetricId = keyof typeof defs;

export const metricDefs: Record<MetricId, MetricDef> = defs;

export function getMetricDef(id: MetricId): MetricDef {
  return metricDefs[id];
}
